// Git Log Tool - Gets commit history from the Git repository
// Extends WorkspaceTool - has access to appType, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitLogTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitLogTool';
  }
  /**
   * Get commit history from the Git repository
   * @param {number} [limit=100] - Maximum number of commits to return
   * @param {string} [ref='HEAD'] - Git reference to get log from
   * @returns {Promise<Object>} Result with commit history
   */
  async gitLog(limit = 100, ref = "HEAD") {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitLogForBrowser(limit, ref);
      }
      if (this.appType === "local") {
        return await this.gitLogForLocal(limit, ref);
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitLog`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitlog|error",
          color1: "red",
        },
        "Failed to get git log",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git log for browser apps using isomorphic-git
   */
  async gitLogForBrowser(limit, ref) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    // Check if .git exists
    const gitExists = await this.checkGitExists(fs);
    if (!gitExists) {
      return this.returnSuccess(
        {
          commits: [],
          count: 0,
        },
        "No git repository found"
      );
    }

    try {
      // First, try to resolve HEAD to see if there are any commits
      let headOid = null;
      try {
        headOid = await this.git.resolveRef({
          fs: this.fs,
          dir,
          ref: "HEAD",
        });
        logger.log(
          {
            tags: "tools|gitlog|info",
            color1: "lime",
          },
          `Resolved HEAD to: ${headOid}`
        );
      } catch (resolveError) {
        // HEAD doesn't exist or points to nothing - no commits yet
        logger.log(
          {
            tags: "tools|gitlog|info",
            color1: "lime",
          },
          "HEAD not found or invalid, no commits yet"
        );
        return this.returnSuccess(
          {
            commits: [],
            count: 0,
          },
          "No commits found - HEAD not set"
        );
      }

      // Get commit log
      const commits = await this.git.log({
        fs: this.fs,
        dir,
        ref,
        depth: limit,
      });

      logger.log(
        {
          tags: "tools|gitlog|info",
          color1: "lime",
        },
        `Git log returned ${commits.length} commits`,
        {
          commitsCount: commits.length,
          firstCommit: commits[0]
            ? {
                oid: commits[0].oid,
                message: commits[0].commit?.message,
                keys: Object.keys(commits[0]),
                hasMessage: !!commits[0].commit?.message,
                messageType: typeof commits[0].commit?.message,
              }
            : null,
        }
      );

      // Format commits for display
      // isomorphic-git log returns commits with: oid, commit, payload
      // The actual commit data is inside the 'commit' property
      const formattedCommits = commits.map((commit, index) => {
        const commitData = commit.commit;

        // Log the first commit structure for debugging
        if (index === 0) {
          logger.log(
            {
              tags: "tools|gitlog|info",
              color1: "lime",
            },
            "First commit structure details",
            {
              oid: commit.oid,
              hasCommitData: !!commitData,
              commitKeys: commitData ? Object.keys(commitData) : [],
              message: commitData?.message,
              author: commitData?.author,
              committer: commitData?.committer,
            }
          );
        }

        // Check the actual structure of the commit object
        const timestamp =
          commitData?.committer?.timestamp || commitData?.author?.timestamp;
        const date = timestamp
          ? new Date(timestamp * 1000).toISOString()
          : null;

        return {
          hash: commit.oid,
          message: (commitData?.message || "").trim(),
          date: date,
          author: commitData?.author?.name || "Unknown",
          email: commitData?.author?.email || "",
        };
      });

      logger.log(
        {
          tags: "tools|gitlog|info",
          color1: "lime",
        },
        `Formatted ${formattedCommits.length} commits for display`
      );

      return this.returnSuccess(
        {
          commits: formattedCommits,
          count: formattedCommits.length,
        },
        `Retrieved ${formattedCommits.length} commit(s)`
      );
    } catch (error) {
      // Log the full error for debugging
      logger.error(
        {
          tags: "tools|gitlog|error",
          color1: "red",
        },
        "Failed to get git log",
        {
          error: error.message,
          stack: error.stack,
          errorName: error.name,
        }
      );

      // If ref doesn't exist (e.g., no commits yet), return empty
      if (
        error.message &&
        (error.message.includes("not found") ||
          error.message.includes("Could not resolve"))
      ) {
        return this.returnSuccess(
          {
            commits: [],
            count: 0,
          },
          "No commits found"
        );
      }
      throw error;
    }
  }

  /**
   * Commit history for `local` apps via Revolt Desktop (`workspace-local:git-log`).
   * Return shape matches `gitLogForBrowser` (`commits`, `count`).
   */
  async gitLogForLocal(limit, ref) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitLog) {
      return this.returnError(
        {},
        "Local git log requires Revolt Desktop (workspaceLocalAppAPI.gitLog)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const res = await api.gitLog({
      rootPath: root,
      limit,
      ref: ref || "HEAD",
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to read git log");
    }

    const commits = res.commits || [];
    logger.log(
      {
        tags: "tools|gitlog|local|info",
        color1: "lime",
      },
      `Git log retrieved (local): ${commits.length} commit(s)`
    );

    return this.returnSuccess(
      {
        commits,
        count: res.count ?? commits.length,
      },
      `Retrieved ${commits.length} commit(s)`
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }
}
